import type { DocumentResult } from '../shared/desktop-api';

/** One row a tied lookup could mean, and every value it would fill on this document. */
export type TieChoice = {
  rowNumber: number;
  /** What the row writes into the field the choice is shown on; empty when its cell is blank. */
  value: string;
  fills: { fieldName: string; value: string }[];
};

/**
 * The rows a field's lookup tied between, each with what it would fill.
 *
 * A tie is one lookup answering with more than one row, so the fields it fills settle
 * together: picking a row fills every one of them that is not yet confirmed from that row,
 * and no field can take its value from one row while its neighbour takes another's.
 * Empty when the field is confirmed or its lookup did not tie.
 */
export function tieChoices(document: DocumentResult, fieldName: string): TieChoice[] {
  const field = document.fields.find((candidate) => candidate.name === fieldName);
  const evidence = field?.lookupEvidence;
  if (!field || field.verified || !evidence?.choices?.length) return [];
  const siblings = document.fields.filter(
    (candidate) =>
      !candidate.verified &&
      candidate.lookupEvidence?.ruleId === evidence.ruleId &&
      candidate.lookupEvidence.sourceFieldId === evidence.sourceFieldId &&
      candidate.lookupEvidence.choices?.length,
  );
  return evidence.choices.map((choice) => ({
    rowNumber: choice.rowNumber,
    value: choice.value ?? '',
    fills: siblings.flatMap((sibling) => {
      const own = sibling.lookupEvidence?.choices?.find(
        (candidate) => candidate.rowNumber === choice.rowNumber,
      );
      return own ? [{ fieldName: sibling.name, value: own.value ?? '' }] : [];
    }),
  }));
}
